import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedName from 'I18n/components/FormattedName';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import Small from 'UIComponents/elements/Small';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { useUser } from '../hooks/useUser';
import { EERIE } from 'HubStyleTokens/colors';
import NextBestActions from './NextBestActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FormattedParagraphs = styled.div.withConfig({
  displayName: "CallSummaryContent__FormattedParagraphs",
  componentId: "sc-17z4gsn-0"
})(["p{word-wrap:break-word;min-height:17px;margin-bottom:0;}"]);
const Wrapper = styled.div.withConfig({
  displayName: "CallSummaryContent__Wrapper",
  componentId: "sc-17z4gsn-1"
})(["display:flex;align-items:center;div{margin:0;}"]);
const CustomLoadingOverlayWrapper = styled.div.withConfig({
  displayName: "CallSummaryContent__CustomLoadingOverlayWrapper",
  componentId: "sc-17z4gsn-2"
})(["position:relative;height:", "px;min-height:75px;margin:4px 0 8px;inset:0;display:flex;justify-content:center;align-items:center;flex-direction:column;color:", ";"], ({
  overlayHeight
}) => overlayHeight, EERIE);
const CallSummaryContent = ({
  callSummary,
  lastUpdatedAtTimestamp,
  lastUpdatedByUserId,
  isLoadingCallSummaryHistory,
  isExtendedLoadingCallSummary,
  fetchCallSummaryHistoryError,
  nextBestActions,
  isUngatedForNextBestActions,
  nextBestActionsLoadingStatus,
  refetchNextBestActions,
  shouldShowNextBestActions
}) => {
  const {
    user,
    loading: isLoadingUser,
    error: fetchUserError
  } = useUser({
    userId: lastUpdatedByUserId
  });
  const [overlayHeight, setOverlayHeight] = useState(0);
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current && !isExtendedLoadingCallSummary) {
      setOverlayHeight(contentRef.current.clientHeight);
    }
  }, [isExtendedLoadingCallSummary]);
  const renderPropertyHistory = () => {
    if (isLoadingCallSummaryHistory || isLoadingUser) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "xs",
        "data-test-id": "loading-spinner"
      });
    } else if (fetchCallSummaryHistoryError || fetchUserError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.error"
      });
    } else {
      const Name = user ? /*#__PURE__*/_jsx(FormattedName, {
        givenName: user.firstName,
        familyName: user.lastName
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.unknownUser"
      });
      const DateTime = lastUpdatedAtTimestamp ? /*#__PURE__*/_jsx(FormattedDateTime, {
        value: new Date(lastUpdatedAtTimestamp),
        format: "lll"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.unknownDate"
      });
      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "call-summary.lastUpdated",
        options: {
          Name,
          DateTime
        }
      });
    }
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: isExtendedLoadingCallSummary ? /*#__PURE__*/_jsxs(CustomLoadingOverlayWrapper, {
      overlayHeight: overlayHeight,
      children: [/*#__PURE__*/_jsx(UILoadingSpinner, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.generating.message"
      })]
    }) : /*#__PURE__*/_jsx(FormattedParagraphs, {
      ref: contentRef,
      children: callSummary && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("h5", {
          style: {
            marginTop: '16px'
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "call-summary.header"
          })
        }), /*#__PURE__*/_jsx(SanitizedText, {
          body: callSummary,
          className: "m-top-3"
        }), isUngatedForNextBestActions && shouldShowNextBestActions && /*#__PURE__*/_jsx(NextBestActions, {
          nextBestActions: nextBestActions,
          nextBestActionsLoadingStatus: nextBestActionsLoadingStatus,
          refetchNextBestActions: refetchNextBestActions
        }), /*#__PURE__*/_jsx("div", {
          className: "m-top-2",
          children: /*#__PURE__*/_jsxs(Wrapper, {
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: "artificialIntelligence",
              size: 12,
              className: "is--text--help p-right-1"
            }), /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: renderPropertyHistory()
            })]
          })
        })]
      })
    })
  });
};
export default CallSummaryContent;